import type { BloomRuntimeConfig, BloomUser } from '@apm/contracts';
import type { NativeBridge, NativeSignIn } from '../native/bridge.js';

/**
 * 로그인 입구를 무엇으로 그리는가 (v17 R4 · `docs/40` §5.1).
 *
 * ── 왜 판정이 하나인가 ───────────────────────────────────────
 *
 * `loginEntryClientId`(v9 W10)를 **흡수한다.** 입구가 둘(게이트 화면 · 설정 시트)인데
 * 판정을 나누면 **「어느 입구로 들어왔느냐에 따라 로그인 뒤가 다르다」**가 시작되고,
 * 그것은 `SettingsSheet` 의 주석이 이미 경계한 실패다. v17 이 갈래를 셋에서 여섯으로
 * 늘리므로 그 위험도 그만큼 커진다.
 *
 * **순수 함수로 떼어 둔다** — 이 패키지는 node 환경에서 테스트한다(jsdom 미도입).
 * `client-type.ts`·`bridge.ts` 와 같은 자리다.
 *
 * ── 갈래가 여섯인 이유 ───────────────────────────────────────
 *
 * ```
 * none           로그인한 계정 — 입구를 그리지 않는다
 * native         셸이 signIn 을 연다                    ← v17 (docs/40 D1)
 * shellTooOld    셸인데 signIn 이 없다                  ← v17 (docs/40 D3)
 * gisRedirect    홈 화면 PWA — 팝업이 못 돌아온다       ← v17 (docs/40 D11)
 * gis            그 밖 — 지금 그대로 팝업
 * notConfigured  이 배포에 클라이언트 ID 가 없다
 * ```
 */
export type LoginMode =
  | { kind: 'none' }
  /**
   * **판정이 아니라 값을 돌려준다** (v9 `loginEntryClientId` 의 방침 승계). 「셸이다」만
   * 답하면 호출부가 `getNativeBridge()?.signIn` 을 다시 좁혀야 하고, 그 자리에서
   * `?? (() => …)` 같은 거짓말이 생긴다.
   */
  | { kind: 'native'; signIn: () => Promise<NativeSignIn> }
  | { kind: 'shellTooOld' }
  | { kind: 'gisRedirect'; clientId: string }
  | { kind: 'gis'; clientId: string }
  | { kind: 'notConfigured' };

/**
 * @param bridge 셸 안이면 브리지, 아니면 `null`. **읽는 것은 `signIn` 하나다** —
 *   판정에 필요한 사실이 그것뿐이고, 넓게 받으면 이 함수가 DOM 을 아는 쪽으로 끌려간다.
 * @param redirectLogin 팝업이 결과를 돌려주지 못하는 화면인가. **호출부가 판정해 넘긴다** —
 *   `navigator.standalone` 도 `matchMedia` 도 이 함수가 읽을 수 없다. 그 판정 자체는
 *   `needsRedirectLogin()` 이 하고, **그것은 순수 함수라 테스트가 본다** (아래 ★).
 */
export function loginMode(
  runtime: Pick<BloomRuntimeConfig, 'googleClientId'>,
  user: BloomUser | null,
  bridge: Pick<NativeBridge, 'signIn'> | null,
  redirectLogin: boolean,
): LoginMode {
  /*
   * 조건 1 (v9 승계). 로그인한 계정에 로그인 버튼을 또 그리면 계정을 바꾸는 것인지 다시
   * 묶는 것인지 알 수 없다 — 계정을 바꾸는 경로는 로그아웃을 거친다.
   */
  if (user !== null) return { kind: 'none' };

  /*
   * **셸에서는 `googleClientId` 를 보지 않는다.** 셸은 자기 iOS 클라이언트 ID 를
   * `Info.plist` 로 갖고 서버가 그것으로 검증한다(`docs/40` D7) — 웹 클라이언트 ID 가
   * 없는 배포에서도 셸 로그인은 성립한다.
   */
  if (bridge !== null) {
    if (bridge.signIn !== null) return { kind: 'native', signIn: bridge.signIn };
    /*
     * **GIS 로 폴백하지 않는다.** WKWebView 안의 GIS 는 구글이 막으므로 그리면 「버튼이
     * 조용히 안 뜬다」가 되고, 그것이 `docs/29` D8 이 처음부터 지목한 침묵이다.
     * 여기서는 「이 앱으로는 못 한다」고 말하는 편이 낫다 — 할 일이 재설치 하나로 정해진다.
     */
    return { kind: 'shellTooOld' };
  }

  /*
   * 조건 2 (v9 승계). 없는 것은 계정을 쓰지 않는 배포의 **정상 구성**이다
   * (`docs/12` §2.4). 그때 버튼을 그리면 눌러도 아무 일이 일어나지 않는다 — GIS 는
   * 클라이언트 ID 없이 서지 않는다.
   */
  const clientId = runtime.googleClientId;
  if (clientId === null || clientId === '') return { kind: 'notConfigured' };

  /*
   * 홈 화면 앱의 GIS 팝업은 **별도 브라우저 맥락**으로 열려 `postMessage` 로 돌아오지
   * 못한다 (`docs/15` §412). 그래서 그 화면에서만 redirect 모드다 — **PC·Android 의
   * 팝업은 건드리지 않는다**(`docs/40` D13). 팝업 경로는 실기로 증명된 유일한 로그인이고
   * (`docs/21` §12.1), 전부 옮기면 증명된 것을 미증명으로 바꾼다.
   */
  return redirectLogin ? { kind: 'gisRedirect', clientId } : { kind: 'gis', clientId };
}

/**
 * ★ **팝업이 결과를 돌려주지 못하는 화면인가** (2026-08-19).
 *
 * ── 두 번 좁혔다 ────────────────────────────────────────────
 *
 * 위 주석이 **PC·Android 의 팝업은 건드리지 않는다**(`docs/40` D13)고 적어 두었는데,
 * 넘기는 값이 `isStandaloneDisplay()` 하나여서 **설치하기만 하면 전부 redirect** 로 갔다.
 * 그리고 그 경로는 이 배포에서 성립하지 않는다 — GIS 는 `g_csrf_token` 쿠키를 **앱
 * 오리진**(`bloom.bloomlabs.tv`)에 심는데 구글은 **API 오리진**(`bloom-api…`)으로 POST
 * 하므로 쿠키가 따라가지 않는다(`docs/40` §8-⑬). 서버 로그에
 * **`CSRF 대조 실패 (쿠키 없음)`** 이 남고 **화면에는 아무 오류도 뜨지 않는다.**
 *
 * 노트북 PWA 에서 먼저 걸렸고(그래서 「설치된 앱 + 손으로 쓰는 기기」로 좁혔다),
 * 곧이어 **Android 홈 화면 앱에서 같은 로그**가 나왔다 — 좁히는 축이 틀렸던 것이다.
 *
 * ── 지금 아는 것은 iOS 하나다 ────────────────────────────────
 *
 * 「팝업이 돌아오지 못한다」의 근거는 **iOS 사례 보고 하나뿐이고**(`docs/15` §7 —
 * 「iOS standalone PWA 의 GIS」), **Android WebAPK 에 대한 증거는 이 저장소에 없다.**
 * 그래서 redirect 는 **iOS 홈 화면 앱에만** 남긴다.
 *
 * `navigator.standalone` 은 **iOS 에만 있는 비표준 속성**이라 이 하나가 곧 판정이다 —
 * `display-mode: standalone` 은 데스크톱 PWA 도 Android WebAPK 도 참이라 쓸 수 없다.
 * (`tv2m-client-sdk` 의 `needsIosInstallForPush` 가 같은 이유로 같은 속성을 본다.)
 *
 * **판정을 순수 함수로 두는 이유가 이번 결함 자체다.** 사실을 읽는 자리와 규칙을 정하는
 * 자리가 붙어 있으면 **규칙이 틀려도 테스트가 볼 것이 없다.**
 */
export function needsRedirectLogin(facts: DisplayFacts): boolean {
  return facts.iosHomeScreenApp;
}

export interface DisplayFacts {
  /**
   * iOS 홈 화면 앱으로 실행 중인가.
   *
   * **설치 여부가 아니다** — 데스크톱 PWA 와 Android WebAPK 는 여기서 거짓이고, 둘 다
   * 증명된 팝업 경로로 간다.
   */
  iosHomeScreenApp: boolean;
}

/**
 * 화면에 대한 사실을 읽는다. **DOM 을 읽는 자리는 여기 하나다** (`needsRedirectLogin` 이
 * 규칙을 갖는다).
 */
export function readDisplayFacts(): DisplayFacts {
  if (typeof window === 'undefined') return { iosHomeScreenApp: false };
  return {
    iosHomeScreenApp:
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true,
  };
}
